/**
 * errorReporter.js
 *
 * Lightweight self-hosted crash reporting. Sends uncaught errors and
 * unhandled promise rejections to POST /api/client-errors so crashes on
 * users' devices (especially mobile) leave evidence in our database.
 *
 * Import once for side-effects (installs global listeners), or call
 * reportClientError() directly (used by ErrorBoundary).
 */
const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAX_REPORTS_PER_SESSION = 5;
const seenMessages = new Set();
let reportCount = 0;

// Benign browser noise we never want to log
const IGNORED_PATTERNS = [
  /ResizeObserver loop/i,
  /Loading chunk \d+ failed/i,
];

export function reportClientError({ message, stack, source, componentStack }) {
  try {
    const msg = String(message || 'Unknown error').slice(0, 1000);
    if (reportCount >= MAX_REPORTS_PER_SESSION) return;
    if (seenMessages.has(msg)) return;
    if (IGNORED_PATTERNS.some((re) => re.test(msg))) return;
    seenMessages.add(msg);
    reportCount += 1;

    fetch(`${API_URL}/client-errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: msg,
        stack: stack ? String(stack).slice(0, 8000) : null,
        source: source || 'window',
        url: window.location.href.slice(0, 500),
        component_stack: componentStack ? String(componentStack).slice(0, 4000) : null,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
      }),
      keepalive: true, // survive page unload
    }).catch(() => {});
  } catch (_) {
    // Reporting must never crash the app
  }
}

// --- Global listeners (installed on import) ---
window.addEventListener('error', (event) => {
  reportClientError({
    message: event.message || (event.error && event.error.message),
    stack: event.error && event.error.stack,
    source: 'window',
  });
});

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  reportClientError({
    message: (reason && (reason.message || String(reason))) || 'Unhandled promise rejection',
    stack: reason && reason.stack,
    source: 'promise',
  });
});
